import { useState } from 'react'
import { Check, X } from 'lucide-react'
import StateMessage from '@/components/StateMessage'
import { logger } from '@/utils/logger'
import { STRINGS } from '@/constants'
import type { ExchangeFormData } from '@/types'

const CTX = 'ExchangeContactsModal'

interface Props {
  onClose: () => void
  onSubmit: (data: ExchangeFormData) => Promise<void>
}

const EMPTY: ExchangeFormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  note: '',
}

export default function ExchangeContactsModal({ onClose, onSubmit }: Props) {
  const [form, setForm] = useState<ExchangeFormData>(EMPTY)
  const [honeypot, setHoneypot] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const set =
    (field: keyof ExchangeFormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [field]: e.target.value }))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (honeypot) {
      logger.warn(CTX, 'honeypot triggered — bot detected')
      setSubmitted(true) // silent success to the bot
      return
    }
    logger.info(CTX, 'form submit', { email: form.email })
    setSubmitting(true)
    setError(null)
    try {
      await onSubmit(form)
      logger.info(CTX, 'form submit success')
      setSubmitted(true)
    } catch (err) {
      logger.error(CTX, 'form submit failed', err)
      setError(err instanceof Error ? err.message : STRINGS.qr.errorGeneric)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="modal-backdrop">
      <div className="modal-container">
        <div className="modal-header">
          <h2 className="modal-title">{STRINGS.form.title}</h2>
          <button onClick={onClose} aria-label="Close" className="btn-icon">
            <X className="w-5 h-5" />
          </button>
        </div>

        {submitted ? (
          <StateMessage
            tone="green"
            icon={<Check className="w-7 h-7" strokeWidth={2.5} />}
            title={STRINGS.qr.contactSent}
            description={STRINGS.qr.contactSentDesc}
          />
        ) : (
          <form onSubmit={handleSubmit} className="modal-body">
            {/* Honeypot — invisible to real users */}
            <input
              name="_hp"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              className="hidden"
              aria-hidden="true"
            />

            <div className="grid grid-cols-2 gap-3">
              <Field
                label={STRINGS.form.firstName}
                required
                value={form.firstName}
                onChange={set('firstName')}
                placeholder={STRINGS.form.firstNamePlaceholder}
              />
              <Field
                label={STRINGS.form.lastName}
                required
                value={form.lastName}
                onChange={set('lastName')}
                placeholder={STRINGS.form.lastNamePlaceholder}
              />
            </div>
            <Field
              label={STRINGS.form.email}
              required
              type="email"
              value={form.email}
              onChange={set('email')}
              placeholder={STRINGS.form.emailPlaceholder}
            />
            <Field
              label={STRINGS.form.phone}
              value={form.phone}
              onChange={set('phone')}
              placeholder={STRINGS.form.phonePlaceholder}
            />

            <div className="field">
              <label className="field-label">{STRINGS.form.note}</label>
              <textarea
                value={form.note}
                onChange={set('note')}
                placeholder={STRINGS.form.notePlaceholder}
                rows={2}
                className="field-textarea"
              />
            </div>

            {error && <p className="text-sm text-error">{error}</p>}

            <button
              type="submit"
              disabled={submitting}
              className="btn btn-sm btn-primary mt-1"
            >
              {submitting ? STRINGS.form.sending : STRINGS.form.shareMyContact}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

interface FieldProps {
  label: string
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  placeholder?: string
  required?: boolean
  type?: string
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  required,
  type = 'text',
}: FieldProps) {
  return (
    <div className="field">
      <label className="field-label">
        {label}
        {required && <span className="field-required">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        className="field-input"
      />
    </div>
  )
}
